import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles, Send, Loader2, X, ArrowRight, MapPin } from 'lucide-react';
import clsx from 'clsx';
import { copilotService, CopilotMessage, CopilotPageContext } from '@/services/copilot.service';
import { collectPageContext, starterQuestions } from '@/utils/pageContext';

/**
 * Conversational copilot — global slide-over panel triggered by ⌘K / Ctrl+K
 * or the topbar button. Talks to the existing AI service via /copilot/ask
 * with a tenant-scoped system prompt baked in by the backend.
 *
 * Memory: chat history is kept in component state for the session — closing
 * the panel resets it. Persisting across reloads is a follow-up.
 */
export const CopilotPanel: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [actions, setActions] = useState<Array<{ label: string; href: string }>>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const { pathname } = useLocation();
  // What the user is looking at. Captured when the panel opens (not on every
  // keystroke) so the answer describes the screen they asked from.
  const [page, setPage] = useState<CopilotPageContext | null>(null);
  const [usePage, setUsePage] = useState(true);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Snapshot the page each time the panel opens, and reset the thread when the
  // user moves to a different screen — answers about the old page would mislead.
  useEffect(() => {
    if (!open) return;
    setPage(collectPageContext(pathname));
    setUsePage(true);
  }, [open, pathname]);

  useEffect(() => { setMessages([]); setActions([]); }, [pathname]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 50);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const submit = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    const next: CopilotMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setBusy(true);
    try {
      const r = await copilotService.ask(next, usePage && page ? page : undefined);
      setMessages([...next, { role: 'assistant', content: r.reply }]);
      setActions(r.actions ?? []);
    } catch {
      setMessages([...next, { role: 'assistant', content: 'Sorry — that request failed. Try again or check your AI provider keys in Settings.' }]);
    } finally {
      setBusy(false);
    }
  };

  // Height follows content: reset, then take scrollHeight, capped by max-h via CSS.
  const autoGrow = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 152)}px`;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // Page-specific prompts first — someone opening the copilot while stuck on a
  // form wants help with THAT, not a report on their win rate.
  const suggestions = useMemo(() => {
    const generic = [
      'How many open leads do I have?',
      "What's my win rate this month?",
      'How do I create an email sequence?',
    ];
    const local = usePage && page ? starterQuestions(page) : [];
    return [...local, ...generic].slice(0, 5);
  }, [usePage, page]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative flex h-full w-full max-w-md flex-col border-l border-border bg-surface shadow-elevated animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-soft text-primary">
              <Sparkles size={15} />
            </span>
            <div>
              <div className="font-display text-sm font-semibold text-text-primary">Copilot</div>
              <div className="text-2xs text-text-muted">Ask anything about your CRM</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-text-muted hover:bg-surface-alt hover:text-text-primary"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>

        {/* What the copilot is looking at. Dismissible: sometimes the question
            has nothing to do with the current screen. */}
        {page && (
          <div className="border-b border-border px-5 py-2">
            {usePage ? (
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-primary-soft px-2.5 py-1 text-2xs text-primary">
                <MapPin size={11} className="shrink-0" />
                <span className="truncate">
                  Looking at: {page.title ?? page.route}
                  {page.fields.length > 0 && ` · ${page.fields.length} field${page.fields.length === 1 ? '' : 's'}`}
                </span>
                <button
                  onClick={() => setUsePage(false)}
                  className="ml-0.5 shrink-0 rounded-full p-0.5 hover:bg-primary/15"
                  aria-label="Ignore the current page"
                  title="Answer without this page's context"
                >
                  <X size={10} />
                </button>
              </span>
            ) : (
              <button
                onClick={() => setUsePage(true)}
                className="text-2xs text-text-muted underline-offset-2 hover:text-primary hover:underline"
              >
                Use this page as context
              </button>
            )}
          </div>
        )}

        {/* Conversation */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {messages.length === 0 && (
            <div className="space-y-3">
              {usePage && page?.title ? (
                <div className="space-y-2">
                  <p className="text-sm text-text-secondary">
                    You're on <span className="font-medium text-text-primary">{page.title}</span>
                    {page.purpose ? ` — ${page.purpose}` : '.'}
                  </p>
                  {page.actions && page.actions.length > 0 && (
                    <ul className="space-y-1 text-xs text-text-secondary">
                      {page.actions.slice(0, 4).map((a) => (
                        <li key={a} className="flex gap-2">
                          <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-text-muted" aria-hidden />
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <p className="text-sm text-text-secondary">
                  Hi — I'm your CRM copilot. I can summarise your data, explain how to do things, or jump you to the right page.
                </p>
              )}
              <div className="space-y-1.5">
                <p className="text-2xs font-semibold uppercase tracking-wider text-text-muted">Try:</p>
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => { setInput(s); setTimeout(submit, 50); }}
                    className="block w-full rounded-lg border border-border bg-surface-alt/40 px-3 py-2 text-left text-xs text-text-secondary hover:border-primary/40 hover:text-text-primary transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={clsx('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
              <div className={clsx(
                'max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap',
                m.role === 'user'
                  ? 'bg-primary text-text-on-primary'
                  : 'bg-surface-alt text-text-primary',
              )}>
                {m.content}
              </div>
            </div>
          ))}

          {busy && (
            <div className="flex justify-start">
              <div className="inline-flex items-center gap-2 rounded-2xl bg-surface-alt px-3 py-2 text-sm text-text-muted">
                <Loader2 size={13} className="animate-spin" /> thinking…
              </div>
            </div>
          )}

          {/* Suggested actions after assistant reply */}
          {!busy && actions.length > 0 && messages[messages.length - 1]?.role === 'assistant' && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {actions.map((a) => (
                <Link
                  key={a.href}
                  to={a.href}
                  onClick={onClose}
                  className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary-soft px-2.5 py-1 text-2xs font-semibold text-primary hover:bg-primary-soft/70"
                >
                  {a.label} <ArrowRight size={10} />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-border bg-surface p-3">
          <div className="flex items-end gap-2 rounded-xl border border-border bg-surface-alt/40 p-2 focus-within:border-primary">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => { setInput(e.target.value); autoGrow(e.currentTarget); }}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Ask about leads, deals, automation, anything…"
              // Starts as one line and grows with the text up to ~6 lines, then scrolls.
              className="max-h-[9.5rem] min-h-[1.5rem] flex-1 resize-none overflow-y-auto bg-transparent py-1 text-sm leading-6 text-text-primary placeholder:text-text-muted focus:outline-none"
              disabled={busy}
            />
            <button
              onClick={submit}
              disabled={!input.trim() || busy}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-text-on-primary disabled:opacity-40"
              aria-label="Send"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
          <div className="mt-1.5 flex items-center justify-between text-2xs text-text-muted">
            <span>Press <kbd className="rounded bg-surface-alt px-1 font-mono">⌘K</kbd> anywhere to toggle</span>
            <span>Enter to send · Shift+Enter for newline</span>
          </div>
        </div>
      </div>
    </div>
  );
};
